// Exibição segura de e-mail (Fase 7).
//
// O problema: o corpo de um e-mail é HTML escrito por um estranho. Qualquer
// pessoa com o nosso endereço consegue mandar `<script>` para dentro do
// VigorHub, e o atendente vai abrir. Diferente do WhatsApp, onde o corpo é
// texto puro e não há o que executar.
//
// A defesa NÃO é limpar o HTML e confiar no resultado. Sanitizador é uma corrida
// entre quem escreve a lista de proibidos e quem inventa um jeito novo — e quem
// inventa só precisa acertar uma vez. A defesa é o navegador: o corpo vai para
// um `<iframe sandbox>` SEM `allow-scripts`, que não executa nada, e sem
// `allow-same-origin`, que o impede de enxergar a sessão mesmo se executasse.
//
// A limpeza abaixo é a segunda camada, não a primeira.

/**
 * Política de conteúdo aplicada DENTRO do iframe.
 *
 * `default-src 'none'` é o ponto de partida: nada carrega, e o que precisa é
 * liberado um a um. Sem isso, um e-mail buscaria uma fonte, uma folha de estilo
 * ou um `fetch` de fora — e cada requisição dessas conta ao remetente que a
 * mensagem foi aberta, com hora e endereço de IP.
 *
 * @param carregarRemotas quando o atendente pede explicitamente para ver as
 *   imagens. Fica desligado por padrão: o "pixel de rastreamento" é uma imagem
 *   de 1x1 invisível cujo único propósito é avisar que você abriu.
 */
export function politicaDeConteudo(carregarRemotas: boolean): string {
  const imagens = carregarRemotas
    ? "img-src data: https: cid:"
    : "img-src data: cid:";
  return [
    "default-src 'none'",
    imagens,
    // O estilo embutido é o que faz o e-mail parecer um e-mail. É seguro aqui
    // porque, sem scripts, CSS não exfiltra nada — e as fontes e imagens que
    // ele tentaria buscar já estão barradas pelas outras diretivas.
    "style-src 'unsafe-inline'",
    "font-src data:",
    // Formulário dentro de e-mail é phishing em 100% dos casos que importam.
    "form-action 'none'",
    // Impede que o conteúdo tente escapar do quadro, reabrindo a página inteira.
    "frame-ancestors 'none'",
  ].join('; ');
}

/**
 * Remove o que não deveria estar num corpo de e-mail.
 *
 * Roda ANTES do iframe, e por dois motivos que não são "impedir a execução" —
 * disso o sandbox já cuida:
 *
 *  1. Se um dia alguém acrescentar `allow-scripts` ao iframe (por engano, ou
 *     para fazer alguma coisa funcionar), esta camada ainda estará aqui.
 *  2. O que sobra fica legível na ferramenta de desenvolvimento, para quem for
 *     investigar um e-mail suspeito.
 *
 * Não é um sanitizador completo e não pretende ser. Um sanitizador completo é
 * o DOMPurify, e ele nem está declarado no projeto.
 */
export function limparHtmlDeEmail(html: string): string {
  return String(html ?? '')
    // Blocos inteiros, com o conteúdo: o que está dentro de <script> não é texto
    // que se queira mostrar.
    .replace(/<script\b[\s\S]*?<\/script\s*>/gi, '')
    .replace(/<style\b[\s\S]*?<\/style\s*>/gi, (m) => m) // estilo fica: é a aparência
    .replace(/<iframe\b[\s\S]*?<\/iframe\s*>/gi, '')
    .replace(/<object\b[\s\S]*?<\/object\s*>/gi, '')
    .replace(/<embed\b[^>]*>/gi, '')
    .replace(/<form\b[\s\S]*?<\/form\s*>/gi, '')
    // `<base href>` reescreve todo link relativo do documento — inclusive para
    // um domínio do atacante.
    .replace(/<base\b[^>]*>/gi, '')
    // <meta http-equiv="refresh"> redireciona o quadro sozinho.
    .replace(/<meta\b[^>]*http-equiv\s*=\s*["']?refresh[^>]*>/gi, '')
    // Manipuladores de evento: onclick, onerror, onload...
    // `onerror` num <img> quebrado é o caminho clássico, e não precisa de clique.
    .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, '')
    .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '')
    // javascript: e data: em href — o segundo permite embutir uma página HTML
    // inteira dentro do link.
    .replace(/(href|src)\s*=\s*(["'])\s*javascript:[^"']*\2/gi, '$1="#"')
    .replace(/href\s*=\s*(["'])\s*data:[^"']*\1/gi, 'href="#"');
}

/**
 * O documento completo que vai no `srcdoc` do iframe.
 *
 * `target="_blank"` na base: link de e-mail abre em aba nova, nunca substituindo
 * o VigorHub. `rel` impede que a página aberta ganhe controle da nossa.
 */
export function montarDocumento(html: string, carregarRemotas: boolean, escuro: boolean): string {
  const corpo = limparHtmlDeEmail(html);
  const fundo = escuro ? '#1e293b' : '#ffffff';
  const texto = escuro ? '#e2e8f0' : '#1f2937';

  return `<!doctype html><html><head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${politicaDeConteudo(carregarRemotas)}">
<base target="_blank" rel="noopener noreferrer">
<style>
  html,body{margin:0;padding:12px;background:${fundo};color:${texto};
    font:14px/1.55 Inter,system-ui,-apple-system,sans-serif;
    /* E-mail de marketing vem com tabela de 700px de largura fixa. Sem isto, o
       quadro ganha barra horizontal e o texto some para a direita. */
    overflow-wrap:break-word;word-break:break-word}
  img{max-width:100%;height:auto}
  table{max-width:100% !important}
  a{color:#5F6C37}
  blockquote{margin:8px 0;padding-left:10px;border-left:3px solid #cbd5e1;color:#64748b}
</style>
</head><body>${corpo}</body></html>`;
}

/**
 * O e-mail tem imagem que só carrega de fora?
 *
 * Serve para não oferecer o botão "carregar imagens" quando não há nenhuma —
 * um botão que não faz nada ensina o atendente a ignorar botões.
 */
export function temImagemRemota(html: string): boolean {
  return /<img\b[^>]*\bsrc\s*=\s*["']?https?:/i.test(String(html ?? ''));
}
